import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { supabase } from "../supabase";
import { useAuth } from "../contexts/AuthContext";
import { useProfileInfo } from "../contexts/ProfileInfoContext";
import useSnackbarNotification from "../hooks/useSnackbarNotification";
import { IoBookmarkOutline, IoBookmark } from "react-icons/io5";
import JobApplicationModal from "../components/JobApplicationModal";
import ShareDropdown from "../components/ShareDropdown";
import JobPostFormModal from "../components/employer/JobPostFormModal";
import { sendCandidateConfirmation, sendEmployerNotification } from "../services/emailService";
import { checkRateLimit } from "../utils/rateLimiter";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const JobDetail = () => {
    const { id: jobId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuth();
    const { profileInfo } = useProfileInfo();
    const { showSuccess, showError } = useSnackbarNotification();

    // Helper function to capitalize first letter of words
    const capitalizeFirstLetter = (str) => {
        if (!str) return str;
        // Handle hyphenated words like "full-time" -> "Full-time"
        return str.split('-').map(word => 
            word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        ).join('-');
    };

    // Helper function to check if text has meaningful content (not just bullets/whitespace)
    const hasContent = (text) => {
        if (!text) return false;
        // Remove bullet characters and whitespace, check if anything remains
        const cleaned = text.replace(/[●•\-*○◦\s]/g, '');
        return cleaned.length > 0;
    };

    // Helper function to preprocess text: convert bullet characters to markdown list syntax
    const preprocessMarkdown = (text) => {
        if (!text) return '';
        
        const bulletChars = ['●', '•', '○', '◦'];
        
        // Split by newlines first
        const lines = text.split(/\r?\n/);
        const processedLines = [];
        
        for (const line of lines) {
            const trimmedLine = line.trim();
            
            if (!trimmedLine) {
                processedLines.push('');
                continue;
            }
            
            // Check if line already uses markdown list syntax
            if (/^[-*+]\s/.test(trimmedLine)) {
                processedLines.push(trimmedLine);
                continue;
            }
            
            // Check if line contains bullet characters (might be multiple in one line)
            const bulletMatches = [];
            
            // Find all bullet positions by checking each character
            for (let i = 0; i < trimmedLine.length; i++) {
                if (bulletChars.includes(trimmedLine[i])) {
                    // Check if bullet is at start of line or preceded by whitespace
                    if (i === 0 || /\s/.test(trimmedLine[i - 1])) {
                        bulletMatches.push(i);
                    }
                }
            }
            
            if (bulletMatches.length > 0) {
                // Split line by bullet positions
                let lastIndex = 0;
                for (let i = 0; i < bulletMatches.length; i++) {
                    const bulletIndex = bulletMatches[i];
                    const nextBulletIndex = bulletMatches[i + 1] || trimmedLine.length;
                    
                    // Add text before first bullet as paragraph if it exists
                    if (i === 0 && bulletIndex > 0) {
                        const beforeText = trimmedLine.substring(0, bulletIndex).trim();
                        if (beforeText) {
                            processedLines.push(beforeText);
                        }
                    }
                    
                    // Extract content after bullet
                    const content = trimmedLine.substring(bulletIndex + 1, nextBulletIndex).trim();
                    if (content) {
                        processedLines.push(`- ${content}`);
                    }
                    
                    lastIndex = nextBulletIndex;
                }
            } else {
                // No bullets found, treat as regular paragraph
                processedLines.push(trimmedLine);
            }
        }
        
        return processedLines.join('\n');
    };

    // Helper function to render markdown content
    const renderMarkdown = (text) => {
        if (!text) return null;
        
        // Preprocess to convert bullet characters to markdown syntax
        const processedText = preprocessMarkdown(text);
        
        return (
            <div className="prose prose-sm max-w-none text-gray-700">
                <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                        // Customize list styling to match existing design
                        ul: ({ node, ...props }) => (
                            <ul className="list-none space-y-2" {...props} />
                        ),
                        li: ({ node, ...props }) => (
                            <li className="flex items-start gap-3" {...props}>
                                <span className="text-black flex-shrink-0 pt-0.5">●</span>
                                <span className="text-gray-700 flex-1">{props.children}</span>
                            </li>
                        ),
                        p: ({ node, ...props }) => (
                            <p className="text-gray-700 leading-relaxed mb-2" {...props} />
                        ),
                        strong: ({ node, ...props }) => (
                            <strong className="font-semibold text-black" {...props} />
                        ),
                        h1: ({ node, ...props }) => (
                            <h1 className="text-2xl font-bold text-black mb-4" {...props} />
                        ),
                        h2: ({ node, ...props }) => (
                            <h2 className="text-xl font-bold text-black mb-3" {...props} />
                        ),
                        h3: ({ node, ...props }) => (
                            <h3 className="text-lg font-semibold text-black mb-2" {...props} />
                        ),
                        a: ({ node, ...props }) => (
                            <a className="text-black hover:text-gray-700 underline" {...props} />
                        ),
                    }}
                >
                    {processedText}
                </ReactMarkdown>
            </div>
        );
    };
    const [job, setJob] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [applicationStats, setApplicationStats] = useState({
        totalApplications: 0,
        totalViews: 0
    });
    const [similarJobs, setSimilarJobs] = useState([]);
    const [isEditOpen, setIsEditOpen] = useState(false);
    const [hasApplied, setHasApplied] = useState(false);
    const [isApplying, setIsApplying] = useState(false);
    const [isBookmarked, setIsBookmarked] = useState(false);
    const [showApplicationModal, setShowApplicationModal] = useState(false);
    const [showShareDropdown, setShowShareDropdown] = useState(false);
    const [shareDropdownJob, setShareDropdownJob] = useState(null);
    const [applicationFormData, setApplicationFormData] = useState(null);
    const [step1FormData, setStep1FormData] = useState(null);
    const [companyLogoUrl, setCompanyLogoUrl] = useState(null);
    const [showStatusDropdown, setShowStatusDropdown] = useState(false);
    const [isStatusUpdating, setIsStatusUpdating] = useState(false);
    const [statusDisplayLabel, setStatusDisplayLabel] = useState('Job status');
    const statusDropdownRef = useRef(null);
    const [employerOnboardingScreeningQuestions, setEmployerOnboardingScreeningQuestions] = useState(null);

    const statusOptions = [
        { label: 'Active', value: 'published' },
        { label: 'Draft', value: 'draft' },
        { label: 'Archived', value: 'closed' }
    ];
    
    // Check if this is an employer view
    const isEmployerView = location.pathname.includes('/employer/job-detail/');

    useEffect(() => {
        if (jobId) {
            fetchJobData();
            if (isEmployerView) {
                fetchApplicationStats();
            }
            fetchSimilarJobs();
        }
    }, [jobId, isEmployerView]);

    // Check if the user has already applied to or bookmarked this job
    useEffect(() => {
        const checkApplicationStatus = async () => {
            try {
                if (!user?.id || !jobId || isEmployerView) return;
                const { data, error } = await supabase
                    .from('applications')
                    .select('id, status')
                    .eq('user_id', user.id)
                    .eq('job_listing_id', jobId)
                    .limit(1)
                    .maybeSingle();

                if (error && error.code !== 'PGRST116') throw error; // ignore No rows found
                if (data) {
                    setHasApplied(data.status === 'applied');
                    setIsBookmarked(data.status === 'bookmarked');
                } else {
                    setHasApplied(false);
                    setIsBookmarked(false);
                }
            } catch (err) {
                console.error('Failed to check application status:', err);
            }
        };
        checkApplicationStatus();
    }, [user?.id, jobId, isEmployerView]);

    useEffect(() => {
        setStatusDisplayLabel('Job status');
    }, [jobId]);

    useEffect(() => {
        if (!showStatusDropdown) return;
        const handleClickOutside = (event) => {
            if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target)) {
                setShowStatusDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showStatusDropdown]);

    const fetchJobData = async () => {
        try {
            setLoading(true);
            let query = supabase
                .from('internal_job_listings')
                .select('*')
                .eq('id', jobId);

            // For employee view, only show published jobs
            // For employer view, show all jobs they own
            if (isEmployerView) {
                query = query.eq('employer_id', user.id);
            } else {
                query = query.eq('status', 'published');
            }

            const { data, error: supabaseError } = await query.single();

            if (supabaseError) throw supabaseError;
            setJob(data);

            // Fetch company logo dynamically from employer
            await fetchCompanyLogo(data.employer_id, data.logo_filename);

            // If job doesn't have screening questions, try to get employer's onboarding screening questions
            if (!data.screening_questions && !isEmployerView && data.employer_id) {
                await fetchEmployerOnboardingScreeningQuestions(data.employer_id);
            }
        } catch (err) {
            console.error("Error fetching job data:", err);
            setError("Failed to load job data");
        } finally {
            setLoading(false);
        }
    };

    const fetchEmployerOnboardingScreeningQuestions = async (employerId) => {
        try {
            // If current user is the employer, we can access their metadata directly
            if (user?.id === employerId) {
                const onboardingQuestions = user?.user_metadata?.employer_profile?.requirements?.screeningQuestionsList;
                if (onboardingQuestions && Array.isArray(onboardingQuestions) && onboardingQuestions.length > 0) {
                    setEmployerOnboardingScreeningQuestions(onboardingQuestions);
                }
                return;
            }

            // For other employers, try to get screening questions from their most recent job that has them
            // This is a workaround since we can't directly access another user's auth metadata
            const { data: jobsWithScreening, error } = await supabase
                .from('internal_job_listings')
                .select('screening_questions, screening_questions_list')
                .eq('employer_id', employerId)
                .eq('screening_questions', true)
                .not('screening_questions_list', 'is', null)
                .order('created_at', { ascending: false })
                .limit(1)
                .maybeSingle();

            if (!error && jobsWithScreening?.screening_questions_list) {
                const questionsList = Array.isArray(jobsWithScreening.screening_questions_list) 
                    ? jobsWithScreening.screening_questions_list 
                    : [];
                if (questionsList.length > 0) {
                    setEmployerOnboardingScreeningQuestions(questionsList);
                }
            }
        } catch (err) {
            console.error("Error fetching employer onboarding screening questions:", err);
            // Silently fail - this is not critical
        }
    };

    const fetchCompanyLogo = async (employerId, logoFilename) => {
        if (!employerId) return;
        
        try {
            // If current user is the employer, use their metadata first
            if (user?.id === employerId) {
                if (user?.user_metadata?.company_logo_url) {
                    setCompanyLogoUrl(user.user_metadata.company_logo_url);
                    return;
                }
                if (user?.user_metadata?.employer_profile?.companyInfo?.logoUrl) {
                    setCompanyLogoUrl(user.user_metadata.employer_profile.companyInfo.logoUrl);
                    return;
                }
            }
            
            // Fetch from storage - use the same pattern as EmployeeCompanySearch
            const bucket = process.env.REACT_APP_PROFILE_IMAGES_BUCKET || "profile-images";
            
            try {
                // List all files in the employer's folder
                const { data: files, error: listError } = await supabase.storage
                    .from(bucket)
                    .list(employerId, {
                        limit: 100,
                        offset: 0,
                        sortBy: { column: 'created_at', order: 'desc' }
                    });
                
                if (!listError && files && files.length > 0) {
                    // Filter for company logo files
                    const logoFiles = files.filter(f => f.name.startsWith('company_logo_'));
                    
                    if (logoFiles.length > 0) {
                        let logoFile = null;
                        
                        // If we have a logo_filename, try to match it
                        if (logoFilename) {
                            logoFile = logoFiles.find(f => 
                                f.name.endsWith(`_${logoFilename}`) ||
                                f.name.endsWith(logoFilename) ||
                                f.name.includes(logoFilename)
                            );
                        }
                        
                        // If no match found, use the most recent logo file
                        if (!logoFile) {
                            logoFile = logoFiles.sort((a, b) => {
                                const aTime = a.created_at || a.updated_at || '';
                                const bTime = b.created_at || b.updated_at || '';
                                return bTime.localeCompare(aTime);
                            })[0];
                        }
                        
                        if (logoFile) {
                            const logoPath = `${employerId}/${logoFile.name}`;
                            const { data: logoUrlData } = supabase.storage.from(bucket).getPublicUrl(logoPath);
                            if (logoUrlData?.publicUrl) {
                                setCompanyLogoUrl(logoUrlData.publicUrl);
                                return;
                            }
                        }
                    }
                }
            } catch (listErr) {
                console.error("Error listing storage files:", listErr);
            }
        } catch (err) {
            console.error("Error fetching company logo:", err);
        }
    };

    const fetchApplicationStats = async () => {
        try {
            // Fetch application count
            const { count: applicationCount } = await supabase
                .from('internal_applications')
                .select('*', { count: 'exact', head: true })
                .eq('job_listing_id', jobId);

            // For now, we'll use a mock view count since we don't have a views table
            // In a real app, you'd track job views in a separate table
            const mockViewCount = Math.floor(Math.random() * 200) + 50;

            setApplicationStats({
                totalApplications: applicationCount || 0,
                totalViews: mockViewCount
            });
        } catch (err) {
            console.error("Error fetching application stats:", err);
        }
    };

    const fetchSimilarJobs = async () => {
        try {
            if (!job) return;

            const { data, error } = await supabase
                .from('internal_job_listings')
                .select('*')
                .eq('status', 'published')
                .neq('id', jobId)
                .or(`specialty.eq.${job.specialty},employment_type.eq.${job.employment_type}`)
                .limit(3);

            if (error) throw error;
            setSimilarJobs(data || []);
        } catch (err) {
            console.error("Error fetching similar jobs:", err);
        }
    };

    // Helper function to format time ago
    const formatTimeAgo = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffInSeconds = Math.floor((now - date) / 1000);
        
        if (diffInSeconds < 60) return 'Just now';
        if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
        if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
        if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)} days ago`;
        if (diffInSeconds < 31536000) return `${Math.floor(diffInSeconds / 2592000)} months ago`;
        return `${Math.floor(diffInSeconds / 31536000)} years ago`;
    };

    const handleBookmarkToggle = async () => {
        if (!user) {
            showError("Please log in to bookmark jobs");
            return;
        }

        try {
            if (isBookmarked) {
                // Remove bookmark
                const { error: deleteError } = await supabase
                    .from('applications')
                    .delete()
                    .eq('user_id', user.id)
                    .eq('job_listing_id', jobId)
                    .eq('status', 'bookmarked');

                if (deleteError) throw deleteError;
                setIsBookmarked(false);
                showSuccess("Job removed from bookmarks");
            } else {
                // Add bookmark
                const now = new Date().toISOString();
                const { error } = await supabase
                    .from('applications')
                    .upsert({
                        user_id: user.id,
                        job_listing_id: jobId,
                        status: 'bookmarked',
                        stage: 'Applications',
                        application_date: now,
                        last_updated: now
                    }, { onConflict: 'user_id,job_listing_id' });

                if (error) throw error;
                
                setIsBookmarked(true);
                showSuccess("Job bookmarked successfully");
            }
        } catch (err) {
            console.error("Error toggling bookmark:", err);
            showError("Failed to update bookmark. Please try again.");
        }
    };

    const handleApplyToggle = async () => {
        if (!user) {
            alert("Please log in to apply for jobs");
            return;
        }

        if (hasApplied) {
            const confirmed = window.confirm("Are you sure you want to revoke this application?");
            if (!confirmed) {
                return;
            }
            // Withdraw application
            try {
                setIsApplying(true);
                const { error: deleteError } = await supabase
                    .from('applications')
                    .delete()
                    .eq('user_id', user.id)
                    .eq('job_listing_id', jobId);

                if (deleteError) throw deleteError;
                setHasApplied(false);
                setIsBookmarked(false);
                showSuccess("Application withdrawn");
            } catch (err) {
                console.error("Error withdrawing application:", err);
                showError("Failed to withdraw application. Please try again.");
            } finally {
                setIsApplying(false);
            }
        } else {
            // Open application modal (Step 1)
            setShowApplicationModal(true);
        }
    };

    const handleApplicationStep1Next = async (formData) => {
        // Store form data for Step 2
        setStep1FormData(formData);
        // Keep modal open to show Step 2
        // Modal will automatically switch to step 2 when step1Data prop is provided
    };

    const handleApplicationStep2Next = async (formData) => {
        // Form data now includes both step 1 and step 2 answers
        setApplicationFormData(formData);
        setShowApplicationModal(false);
        setStep1FormData(null);
        
        // Submit the complete application
        await submitApplication(formData);
    };

    const submitApplication = async (formData) => {
        if (!user || !formData) return;

        // Rate limit: Max 20 applications per hour per user
        const rateLimitKey = `application_${user.id}`;
        if (!checkRateLimit(rateLimitKey, 20, 3600000)) {
            showError("Rate limit exceeded. You can submit up to 20 applications per hour. Please try again later.");
            return;
        }

        try {
            setIsApplying(true);
            const now = new Date().toISOString();
            
            // Extract step2Answers if they exist (custom screening questions)
            const step2Answers = formData?.step2Answers || {};
            
            // Extract drivers license and visa sponsorship answers
            const driversLicenseAnswer = formData?.driversLicenseAnswer || null;
            const visaSponsorshipAnswer = formData?.visaSponsorshipAnswer || null;
            
            // Extract CV and cover letter URLs
            // formData.cvFile and formData.coverLetterFile are objects with { name, url, path } structure
            // They come from step1Data which is spread in handleStep3Done
            const cvFileObj = formData?.cvFile || null;
            const coverLetterFileObj = formData?.coverLetterFile || null;
            
            const cvUrl = cvFileObj?.url || null;
            const cvFilename = cvFileObj?.name || null;
            const coverLetterUrl = coverLetterFileObj?.url || null;
            const coverLetterFilename = coverLetterFileObj?.name || null;
            
            // Prepare application data
            const applicationPayload = {
                user_id: user.id,
                job_listing_id: jobId,
                status: 'applied',
                stage: 'Applications',
                application_date: now,
                last_updated: now,
                eligible_to_work: step2Answers.eligibleToWork || null,
                clinical_experience: step2Answers.clinicalExperience || null,
                available_on_site: step2Answers.availableOnSite || null,
                screening_answers: Object.keys(step2Answers).length > 0 ? step2Answers : null,
                drivers_license_answer: driversLicenseAnswer,
                visa_sponsorship_answer: visaSponsorshipAnswer,
            };

            // Only add CV and cover letter fields if they exist
            if (cvUrl) {
                applicationPayload.cv_url = cvUrl;
            }
            if (cvFilename) {
                applicationPayload.cv_filename = cvFilename;
            }
            if (coverLetterUrl) {
                applicationPayload.cover_letter_url = coverLetterUrl;
            }
            if (coverLetterFilename) {
                applicationPayload.cover_letter_filename = coverLetterFilename;
            }
            
            // Store application
            const { data: applicationData, error } = await supabase
                .from('applications')
                .upsert(applicationPayload, { onConflict: 'user_id,job_listing_id' })
                .select()
                .single();

            if (error) {
                throw error;
            }
            
            // Send email to candidate
            const candidateName = profileInfo?.firstname && profileInfo?.lastname
                ? `${profileInfo.firstname} ${profileInfo.lastname}`
                : profileInfo?.firstname || profileInfo?.lastname || user?.email?.split('@')[0] || 'Candidate';
            
            sendCandidateConfirmation(
                user.email,
                candidateName,
                job?.title || 'the position',
                job?.company_name || 'the company',
                now
            );

            // Send email to employer (if we have a contact email)
            const employerEmail = job?.contact_email;
            if (employerEmail) {
                const employerName = job?.company_name || "Hiring Manager";
                const applicationLink = `https://doctorsjobs.io/employer/job-detail/${jobId}`;

                sendEmployerNotification(
                    employerEmail,
                    employerName,
                    candidateName,
                    user.email,
                    job?.title || "the position",
                    now,
                    applicationLink
                );
            }
            
            setHasApplied(true);
            setIsBookmarked(false);
            showSuccess("Application submitted successfully");
        } catch (err) {
            console.error("Error submitting application:", err);
            showError("Failed to submit application. Please try again.");
        } finally {
            setIsApplying(false);
            setApplicationFormData(null);
        }
    };

    const handleShareClick = (e, jobData = null) => {
        e.stopPropagation();
        setShareDropdownJob(jobData || job);
        setShowShareDropdown(true);
    };

    const getDisplayStatus = (statusValue) => {
        switch (statusValue) {
            case 'published':
                return 'Active';
            case 'draft':
                return 'Draft';
            case 'closed':
            case 'archived':
                return 'Archived';
            default:
                return 'Active';
        }
    };

    const handleJobStatusChange = async (newStatusValue) => {
        if (!job?.id || job.status === newStatusValue) {
            setShowStatusDropdown(false);
            return;
        }

        try {
            setIsStatusUpdating(true);
            const { error } = await supabase
                .from('internal_job_listings')
                .update({ status: newStatusValue })
                .eq('id', job.id)
                .eq('employer_id', user.id);

            if (error) throw error;

            setJob((prev) => prev ? { ...prev, status: newStatusValue } : prev);
            setStatusDisplayLabel(getDisplayStatus(newStatusValue));
            showSuccess(`Job status updated to ${getDisplayStatus(newStatusValue)}.`);
        } catch (err) {
            console.error("Failed to update job status:", err);
            showError("Unable to update job status. Please try again.");
        } finally {
            setIsStatusUpdating(false);
            setShowStatusDropdown(false);
        }
    };

    if (loading) {
        return (
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="text-center">Loading job details...</div>
            </div>
        );
    }

    if (error || !job) {
        return (
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="text-center text-red-600">{error || "Job not found"}</div>
            </div>
        );
    }

    return (
        <>
        <style>{`
            @media (max-width: 1024px) {
                .job-detail-container {
                    padding: 1rem !important;
                    padding-top: 1rem !important;
                    padding-bottom: 5rem !important;
                }
                .job-detail-header h1 {
                    font-size: 1.5rem !important;
                    margin-bottom: 1rem !important;
                }
                .job-detail-header-actions {
                    flex-direction: column !important;
                    align-items: flex-start !important;
                    gap: 0.75rem !important;
                    margin-top: 1rem !important;
                    width: 100% !important;
                }
                .job-detail-header-actions.is-employer {
                    flex-direction: row !important;
                    justify-content: flex-start !important;
                }
                .job-detail-header-actions-icons {
                    display: flex !important;
                    gap: 0.5rem !important;
                    align-items: center !important;
                }
                .job-detail-header-actions-icons.is-employer {
                    flex-direction: row !important;
                    gap: 0.5rem !important;
                }
                .job-detail-header-actions button {
                    font-size: 0.875rem !important;
                    padding: 0.625rem 1rem !important;
                }
                .job-detail-header-actions .job-detail-apply-button {
                    width: 100% !important;
                    justify-content: center !important;
                }
                .job-detail-info-bar {
                    flex-direction: column !important;
                    gap: 0.75rem !important;
                    padding: 1rem !important;
                }
                .job-detail-info-bar > span {
                    display: none !important;
                }
                .job-detail-info-bar > div {
                    width: 100% !important;
                    justify-content: flex-start !important;
                }
                .job-detail-content {
                    padding: 1rem !important;
                }
                .job-detail-content h2 {
                    font-size: 1.25rem !important;
                    margin-bottom: 1rem !important;
                }
                .job-detail-content h3 {
                    font-size: 1rem !important;
                    margin-bottom: 0.75rem !important;
                }
                .job-detail-sidebar {
                    margin-top: 1rem !important;
                }
                .job-detail-sidebar-card {
                    padding: 1rem !important;
                }
                .job-detail-sidebar-card h3 {
                    font-size: 1rem !important;
                    margin-bottom: 0.75rem !important;
                }
                .job-detail-similar-jobs {
                    margin-top: 2rem !important;
                }
                .job-detail-similar-jobs h2 {
                    font-size: 1.25rem !important;
                    margin-bottom: 1rem !important;
                }
                .job-detail-similar-jobs-grid {
                    grid-template-columns: 1fr !important;
                    gap: 1rem !important;
                }
                .job-detail-similar-job-card {
                    padding: 1rem !important;
                }
                .job-detail-edit-overlay {
                    padding: 1rem !important;
                    align-items: flex-start !important;
                }
                .job-detail-edit-modal {
                    width: 100% !important;
                    max-width: none !important;
                    border-radius: 1.25rem !important;
                }
                .job-detail-edit-header {
                    padding: 1rem !important;
                }
                .job-detail-edit-body {
                    padding: 1rem !important;
                    max-height: 60vh !important;
                }
                .job-detail-edit-grid {
                    gap: 0.75rem !important;
                }
                .job-detail-edit-footer {
                    flex-direction: column !important;
                    align-items: stretch !important;
                    gap: 0.5rem !important;
                    padding: 1rem !important;
                }
                .job-detail-edit-footer button {
                    width: 100% !important;
                }
            }
        `}</style>
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 job-detail-container">
                {/* Job Header */}
                <div className="mb-6 job-detail-header">
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between">
                        <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                                {companyLogoUrl ? (
                                    <div className="w-10 h-10 flex items-center justify-center bg-gray-200 rounded-full border border-gray-200 overflow-hidden flex-shrink-0">
                                        <img 
                                            src={companyLogoUrl} 
                                            alt={`${job.company_name} logo`}
                                            className="w-full h-full object-contain"
                                        />
                                    </div>
                                ) : (
                                    <div className="w-10 h-10 flex items-center justify-center bg-black rounded-full flex-shrink-0">
                                        <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
                                        </svg>
                                    </div>
                                )}
                                <div className="flex items-center gap-2">
                                    {!isEmployerView && job.employer_id ? (
                                        <button
                                            onClick={() => navigate(`/employee/company/${job.employer_id}`)}
                                            className="text-sm text-gray-500 hover:text-black hover:underline cursor-pointer"
                                        >
                                            {job.company_name}
                                        </button>
                                    ) : (
                                        <span className="text-sm text-gray-500">{job.company_name}</span>
                                    )}
                                    <span className="text-gray-300">|</span>
                                    <span className="text-xs text-gray-400">Posted {formatTimeAgo(job.created_at)}</span>
                                </div>
                            </div>
                            <h1 className="text-3xl font-bold text-black mb-6">
                                {job.title}
                            </h1>
                        </div>
                        
                        {/* Action Buttons */}
                        <div className={`flex items-center gap-3 job-detail-header-actions ${isEmployerView ? 'is-employer' : ''}`}>
                            {isEmployerView ? (
                                <>
                                    <div className="relative job-detail-header-actions-icons is-employer">
                                        <button className="p-2 hover:bg-gray-100 rounded-full" onClick={handleShareClick}>
                                            <img src="/Share.svg" alt="Share" className="w-5 h-5" />
                                        </button>
                                        {showShareDropdown && shareDropdownJob?.id === job?.id && (
                                            <ShareDropdown
                                                url={`${window.location.origin}/employer/job-detail/${job?.id || jobId}`}
                                                title={job?.title}
                                                text={`${job?.title} at ${job?.company_name}${job?.location ? ` • ${job?.location}` : ""}`}
                                                onClose={() => {
                                                    setShowShareDropdown(false);
                                                    setShareDropdownJob(null);
                                                }}
                                            />
                                        )}
                                    </div>
                                    <button 
                                        onClick={() => setIsEditOpen(true)}
                                        className="bg-gray-100 hover:bg-gray-200 rounded-full p-2 transition"
                                        aria-label="Edit Job"
                                    >
                                        <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                        </svg>
                                    </button>
                                    <div className="relative" ref={statusDropdownRef}>
                                        <button
                                            type="button"
                                            className="flex items-center justify-between gap-2 rounded-full border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-black min-w-[150px]"
                                            onClick={() => setShowStatusDropdown((prev) => !prev)}
                                        >
                                            <span className="text-sm text-gray-900">
                                                {statusDisplayLabel}
                                            </span>
                                            <svg
                                                className={`w-4 h-4 text-gray-500 transition-transform ${showStatusDropdown ? 'rotate-180' : ''}`}
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                            </svg>
                                        </button>
                                        {showStatusDropdown && (
                                            <div className="absolute right-0 mt-2 w-48 rounded-xl border border-gray-100 bg-white shadow-lg z-10">
                                                {statusOptions.map((option) => (
                                                    <button
                                                        key={option.value}
                                                        type="button"
                                                        className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex items-center justify-between ${
                                                            job.status === option.value ? 'text-black font-medium' : 'text-gray-700'
                                                        }`}
                                                        onClick={() => handleJobStatusChange(option.value)}
                                                        disabled={isStatusUpdating}
                                                    >
                                                        <span>{option.label}</span>
                                                        {job.status === option.value && (
                                                            <svg className="w-4 h-4 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                            </svg>
                                                        )}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="job-detail-header-actions-icons flex items-center gap-2">
                                        <div className="relative">
                                            <button className="p-2 hover:bg-gray-100 rounded-full" onClick={handleShareClick}>
                                                <img src="/Share.svg" alt="Share" className="w-5 h-5" />
                                            </button>
                                            {showShareDropdown && shareDropdownJob?.id === job?.id && (
                                                <ShareDropdown
                                                    url={`${window.location.origin}/job/${job?.id || jobId}`}
                                                    title={job?.title}
                                                    text={`${job?.title} at ${job?.company_name}${job?.location ? ` • ${job?.location}` : ""}`}
                                                    onClose={() => {
                                                        setShowShareDropdown(false);
                                                        setShareDropdownJob(null);
                                                    }}
                                                />
                                            )}
                                        </div>
                                        <button 
                                            className="p-2 hover:bg-gray-100 rounded-full"
                                            onClick={handleBookmarkToggle}
                                            title={isBookmarked ? "Remove bookmark" : "Bookmark job"}
                                        >
                                            {isBookmarked ? (
                                                <IoBookmark className="w-5 h-5 text-black" />
                                            ) : (
                                                <IoBookmarkOutline className="w-5 h-5 text-gray-600" />
                                            )}
                                        </button>
                                    </div>
                                    <button 
                                        onClick={handleApplyToggle}
                                        disabled={isApplying}
                                        className={`hidden md:flex rounded-full px-6 py-3 text-sm font-medium items-center gap-2 job-detail-apply-button ${hasApplied ? 'bg-black text-white' : 'bg-black text-white hover:bg-gray-800'}`}
                                    >
                                        {hasApplied ? 'Revoke Application' : (isApplying ? 'Applying…' : 'Apply for Job')}
                                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M14,3V5H17.59L7.76,14.83L9.17,16.24L19,6.41V10H21V3M19,19H5V5H12V3H5C3.89,3 3,3.9 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V12H19V19Z"/>
                                        </svg>
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                    
                    {/* Job Details Bar - Full Width */}
                    <div className="mt-6 mb-6">
                        <div className="w-full rounded-2xl border border-gray-200 bg-white px-6 py-5 flex items-center job-detail-info-bar">
                            {/* Location */}
                            <div className="flex items-center gap-2 flex-1 justify-center">
                                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                                <span className="text-gray-900 text-base">{job.location || 'Location'}</span>
                            </div>
                            <span className="text-gray-300 mx-2">|</span>
                            {/* Employment Type */}
                            <div className="flex items-center gap-2 flex-1 justify-center">
                                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                </svg>
                                <span className="text-gray-900 text-base">{capitalizeFirstLetter(job.employment_type || 'Permanent')} - {capitalizeFirstLetter(job.work_preference || 'Onsite')}</span>
                            </div>
                            <span className="text-gray-300 mx-2">|</span>
                            {/* Salary */}
                            <div className="flex items-center gap-2 flex-1 justify-center">
                                <img src="/CurrencyCircleDollar.svg" alt="Salary" className="w-5 h-5" />
                                <span className="text-gray-900 text-base">
                                    {(() => {
                                        const salary = job.salary_range || job.budget_for_role;
                                        const currency = job.salary_currency || 'ZAR';
                                        const period = job.salary_period ? job.salary_period.charAt(0).toUpperCase() + job.salary_period.slice(1) : '';
                                        
                                        if (salary) {
                                            // Format numbers with spaces: "500000" → "500 000"
                                            const formatNumber = (num) => {
                                                return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
                                            };
                                            
                                            // Remove R prefix if exists and format numbers
                                            let formattedSalary = salary.replace(/^R\s*/, '');
                                            // Format each number in the range
                                            formattedSalary = formattedSalary.replace(/\d+/g, (match) => formatNumber(match));
                                            
                                            return `${currency} ${formattedSalary}${period ? ` ${period}` : ''}`;
                                        }
                                        return 'Salary';
                                    })()}
                                </span>
                            </div>
                            <span className="text-gray-300 mx-2">|</span>
                            {/* Experience */}
                            <div className="flex items-center gap-2 flex-1 justify-center">
                                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                </svg>
                                <span className="text-gray-900 text-base">{capitalizeFirstLetter(job.experience_level || job.experience_requirements || 'Experience').replace(/-/g, ' ')}</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Main Content */}
                    <div className="lg:col-span-2">
                        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 job-detail-content">
                            <h2 className="text-2xl font-bold text-black mb-6">About the job</h2>
                            
                            <div className="space-y-6">
                                {/* Location or Licence Restrictions - First */}
                                {job.requires_license && job.hiring_restrictions && (
                                    <div>
                                        <h3 className="font-semibold text-black mb-3">Location or Licence Restrictions</h3>
                                        {renderMarkdown(job.hiring_restrictions)}
                                    </div>
                                )}
                                
                                {/* Job Description - Third */}
                                {job.role_description && (
                                    <div>
                                        <h3 className="font-semibold text-black mb-3">Job Description</h3>
                                        {renderMarkdown(job.role_description)}
                                    </div>
                                )}
                                
                                {job.job_description && !job.role_description && (
                                    <div>
                                        <h3 className="font-semibold text-black mb-3">Job Description</h3>
                                        {renderMarkdown(job.job_description)}
                                    </div>
                                )}
                                
                                {/* Role Requirements - Fourth */}
                                {hasContent(job.preferred_candidate) && (
                                    <div>
                                        <h3 className="font-semibold text-black mb-3">Role Requirements</h3>
                                        {renderMarkdown(job.preferred_candidate)}
                                    </div>
                                )}
                                
                                {/* Duties and Responsibilities - Fifth */}
                                {hasContent(job.duties_and_responsibilities) && (
                                    <div>
                                        <h3 className="font-semibold text-black mb-3">Duties and Responsibilities</h3>
                                        {renderMarkdown(job.duties_and_responsibilities)}
                                        
                                        {/* Required Skills - displayed as pills below Duties and Responsibilities */}
                                        {job.required_skills && Array.isArray(job.required_skills) && job.required_skills.length > 0 && (
                                            <div className="mt-6">
                                                <h3 className="font-semibold text-black mb-3">Required Skills</h3>
                                                <div className="flex flex-wrap gap-2">
                                                    {job.required_skills.map((skill, index) => (
                                                        <span 
                                                            key={index}
                                                            className="inline-flex items-center bg-gray-100 text-gray-800 rounded-full px-4 py-2 text-sm font-medium"
                                                        >
                                                            {skill}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                                
                                {/* Required Skills - displayed if Duties and Responsibilities is empty */}
                                {!hasContent(job.duties_and_responsibilities) && job.required_skills && Array.isArray(job.required_skills) && job.required_skills.length > 0 && (
                                    <div>
                                        <h3 className="font-semibold text-black mb-3">Required Skills</h3>
                                        <div className="flex flex-wrap gap-2">
                                            {job.required_skills.map((skill, index) => (
                                                <span 
                                                    key={index}
                                                    className="inline-flex items-center bg-gray-100 text-gray-800 rounded-full px-4 py-2 text-sm font-medium"
                                                >
                                                    {skill}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                
                                {/* Language Requirements - Moved to appear after Required Skills */}
                                {job.language_requirement && Array.isArray(job.language_requirement) && job.language_requirement.length > 0 && (
                                    <div>
                                        <h3 className="font-semibold text-black mb-3">Language Requirements</h3>
                                        <div className="flex flex-wrap gap-2">
                                            {job.language_requirement.map((language, index) => (
                                                <span 
                                                    key={index}
                                                    className="inline-flex items-center bg-gray-100 text-gray-800 rounded-full px-4 py-2 text-sm font-medium"
                                                >
                                                    {language}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                
                                {/* Other fields */}
                                {job.qualifications && (
                                    <div>
                                        <h3 className="font-semibold text-black mb-3">Requirements</h3>
                                        {renderMarkdown(job.qualifications)}
                                    </div>
                                )}
                                
                                {job.experience_requirements && (
                                    <div>
                                        <h3 className="font-semibold text-black mb-3">Experience Requirements</h3>
                                        {renderMarkdown(job.experience_requirements)}
                                    </div>
                                )}
                                
                                {/* Required skills removed on request */}
                                
                                {job.benefits && (
                                    <div>
                                        <h3 className="font-semibold text-black mb-3">Benefits</h3>
                                        {renderMarkdown(job.benefits)}
                                    </div>
                                )}
                                
                                {job.other_preferences && (
                                    <div>
                                        <h3 className="font-semibold text-black mb-3">Other Preferences</h3>
                                        {renderMarkdown(job.other_preferences)}
                                    </div>
                                )}
                                
                                {/* Show basic job info if no detailed descriptions are available */}
                                {!job.qualifications && !job.role_description && !job.job_description && !job.preferred_candidate && (
                                    <div>
                                        <h3 className="font-semibold text-black mb-3">Job Information</h3>
                                        <div className="space-y-2 text-gray-700">
                                            {job.specialty && (
                                                <p><strong>Specialty:</strong> {job.specialty}</p>
                                            )}
                                            {job.employment_type && (
                                                <p><strong>Employment Type:</strong> {capitalizeFirstLetter(job.employment_type)}</p>
                                            )}
                                            {job.work_preference && (
                                                <p><strong>Work Preference:</strong> {job.work_preference}</p>
                                            )}
                                            {job.requires_license && (
                                                <p><strong>License Required:</strong> Yes</p>
                                            )}
                                            {job.sponsorship_available && (
                                                <p><strong>Sponsorship Available:</strong> Yes</p>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Sidebar */}
                    <div className="lg:col-span-1 space-y-6 job-detail-sidebar">
                        {isEmployerView && (
                            <div className="bg-black text-white rounded-3xl p-6 shadow-lg border border-black job-detail-sidebar-card">
                                <div className="space-y-4">
                                    <div>
                                        <h3 className="text-lg font-semibold">Stand out to top medical talent</h3>
                                    </div>
                                    <p className="text-sm text-gray-200 leading-relaxed">
                                        Highlight your listing across Doctors' Jobs to attract the right doctors faster.
                                    </p>
                                    <ul className="space-y-2 text-sm text-gray-100 list-disc pl-5">
                                        <li>Priority placement in search results</li>
                                        <li>Recommended candidate matches</li>
                                        <li>Analytics to track visibility and performance</li>
                                    </ul>
                                    <button
                                        onClick={() => navigate('/employer/pricing')}
                                        className="w-full bg-white text-black font-semibold rounded-full py-3 hover:bg-gray-100 transition-colors"
                                    >
                                        Upgrade job post
                                    </button>
                                </div>
                            </div>
                        )}
                        {/* More about company */}
                        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 job-detail-sidebar-card">
                            <h3 className="text-lg font-bold text-black mb-4">More about company</h3>
                            {job.industry && (
                                <div className="inline-flex items-center bg-green-100 text-green-800 rounded-full px-3 py-1 text-sm font-medium mb-4">
                                    {job.industry}
                                </div>
                            )}
                            <div className="space-y-3">
                                <div>
                                    {!isEmployerView && job.employer_id ? (
                                        <button
                                            onClick={() => navigate(`/employee/company/${job.employer_id}`)}
                                            className="font-semibold text-black hover:text-gray-700 hover:underline cursor-pointer text-left block"
                                        >
                                            {job.company_name}
                                        </button>
                                    ) : (
                                        <div className="font-semibold text-black">{job.company_name}</div>
                                    )}
                                </div>
                                {job.company_url && (
                                    <a href={job.company_url} target="_blank" rel="noopener noreferrer" className="text-black hover:text-gray-700 underline text-sm block">
                                        {job.company_url}
                                    </a>
                                )}
                                <p className="text-gray-700 text-sm leading-relaxed">
                                    {job.company_description || "As an employer, we provide ample opportunities for people who have a passion for serving others, to make a real difference in the quality of life of our citizens."}
                                </p>
                                {/* Contact details intentionally hidden on request */}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Similar Jobs */}
                {similarJobs.length > 0 && (
                    <div className="mt-12 job-detail-similar-jobs">
                        <h2 className="text-2xl font-bold text-black mb-6">Similar jobs</h2>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 job-detail-similar-jobs-grid">
                            {similarJobs.map((similarJob) => (
                                <div key={similarJob.id} className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 job-detail-similar-job-card">
                                    <div className="flex items-start justify-between mb-4">
                                        <div className="inline-flex items-center bg-green-100 text-green-800 rounded-full px-3 py-1 text-sm font-medium">
                                            {similarJob.specialty || 'Healthcare'}
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <div className="relative">
                                                <button className="p-1 hover:bg-gray-100 rounded" onClick={(e) => handleShareClick(e, similarJob)}>
                                                    <img src="/Share.svg" alt="Share" className="w-4 h-4" />
                                                </button>
                                                {showShareDropdown && shareDropdownJob?.id === similarJob.id && (
                                                    <ShareDropdown
                                                        url={`${window.location.origin}/job/${similarJob.id}`}
                                                        title={similarJob.title}
                                                        text={`${similarJob.title} at ${similarJob.company_name}${similarJob.location ? ` • ${similarJob.location}` : ""}`}
                                                        onClose={() => {
                                                            setShowShareDropdown(false);
                                                            setShareDropdownJob(null);
                                                        }}
                                                    />
                                                )}
                                            </div>
                                            <button className="p-1 hover:bg-gray-100 rounded">
                                                <img src="/save (1).svg" alt="Save" className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                    <div className="space-y-2 mb-4">
                                        <div className="flex items-center gap-2">
                                            <img src="/bell-1.svg" alt="Company" className="w-4 h-4" />
                                            {similarJob.employer_id ? (
                                                <button
                                                    onClick={() => navigate(`/employee/company/${similarJob.employer_id}`)}
                                                    className="font-semibold text-black hover:text-gray-700 hover:underline cursor-pointer"
                                                >
                                                    {similarJob.company_name}
                                                </button>
                                            ) : (
                                                <span className="font-semibold text-black">{similarJob.company_name}</span>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <svg className="w-4 h-4 text-gray-500" fill="currentColor" viewBox="0 0 24 24">
                                                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                                            </svg>
                                            <span className="text-gray-600 text-sm">{similarJob.location}</span>
                                        </div>
                                    </div>
                                    <div className="space-y-2 mb-4">
                                        <h4 className="font-semibold text-black">{similarJob.title}</h4>
                                        <p className="text-gray-500 text-sm">Posted {formatTimeAgo(similarJob.created_at)}</p>
                                    </div>
                                    <button 
                                        onClick={() => navigate(`/job/${similarJob.id}`)}
                                        className="w-full bg-black text-white rounded-full px-4 py-2 text-sm font-medium hover:bg-gray-800"
                                    >
                                        View Job
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                {/* Job Application Modal */}
                {!isEmployerView && showApplicationModal && (
                    <JobApplicationModal
                        isOpen={showApplicationModal}
                        onClose={() => {
                            setShowApplicationModal(false);
                            setStep1FormData(null);
                        }}
                        jobId={jobId}
                        jobTitle={job?.title}
                        screeningQuestionsEnabled={
                            Boolean(job?.screening_questions) || 
                            (employerOnboardingScreeningQuestions && employerOnboardingScreeningQuestions.length > 0)
                        }
                        screeningQuestionsList={
                            Array.isArray(job?.screening_questions_list) && job.screening_questions_list.length > 0
                                ? job.screening_questions_list
                                : (Array.isArray(employerOnboardingScreeningQuestions) && employerOnboardingScreeningQuestions.length > 0
                                    ? employerOnboardingScreeningQuestions
                                    : [])
                        }
                        driversLicenseRequired={Boolean(job?.drivers_license_required)}
                        visaSponsorshipOffered={Boolean(job?.visa_sponsorship_offered)}
                        onNext={step1FormData ? handleApplicationStep2Next : handleApplicationStep1Next}
                        step1Data={step1FormData}
                        onBack={() => setStep1FormData(null)}
                    />
                )}

                {/* Edit Job Modal */}
                {isEmployerView && (
                    <JobPostFormModal
                        isOpen={isEditOpen}
                        initialJob={job}
                        employerUser={user}
                        onClose={() => setIsEditOpen(false)}
                        onSuccess={fetchJobData}
                    />
                )}
            </div>
            
            {/* Sticky Apply Button for Mobile */}
            {!isEmployerView && (
                <div className="fixed bottom-0 left-0 right-0 z-50 md:hidden bg-white border-t border-gray-200 shadow-lg px-4 py-3">
                    <button 
                        onClick={handleApplyToggle}
                        disabled={isApplying}
                        className={`w-full rounded-full px-6 py-3 text-sm font-medium flex items-center justify-center gap-2 ${hasApplied ? 'bg-black text-white' : 'bg-black text-white hover:bg-gray-800'}`}
                    >
                        {hasApplied ? 'Revoke Application' : (isApplying ? 'Applying…' : 'Apply for Job')}
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M14,3V5H17.59L7.76,14.83L9.17,16.24L19,6.41V10H21V3M19,19H5V5H12V3H5C3.89,3 3,3.9 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V12H19V19Z"/>
                        </svg>
                    </button>
                </div>
            )}
        </div>
        </>
    );
};

export default JobDetail;
